import React, { useState, useEffect } from 'react';
import { 
    Box, Paper, Typography, Button, Grid, 
    List, ListItem, ListItemIcon, ListItemText, 
    IconButton, Dialog, DialogTitle, DialogContent, 
    TextField, DialogActions, Link 
} from '@mui/material';
import { 
    InsertDriveFile, Add, Delete, OpenInNew, 
    PictureAsPdf, Image, Description 
} from '@mui/icons-material';
import api from '../../../api/axios';
import { parseUtc } from '../../../utils/time';


const FileIcon = ({ type }) => {
    if (!type) return <InsertDriveFile />;
    if (type.includes('pdf')) return <PictureAsPdf color="error" />;
    if (type.includes('image')) return <Image color="primary" />;
    if (type.includes('doc')) return <Description color="primary" />;
    return <InsertDriveFile />;
};

const JobFiles = ({ jobId }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAdd, setOpenAdd] = useState(false);
    
    // Form State
    const [fileName, setFileName] = useState('');
    const [fileUrl, setFileUrl] = useState('');
    const [fileType, setFileType] = useState('other');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        loadFiles();
    }, [jobId]);

    const loadFiles = async () => {
        try {
            const res = await api.get(`/admin/jobs/${jobId}/files`);
            setFiles(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleAddFile = async () => {
        if (!fileName || !fileUrl) return;
        setUploading(true);
        try {
            await api.post(`/admin/jobs/${jobId}/files`, {
                file_name: fileName,
                url: fileUrl,
                file_type: fileType,
                description: 'Added via web portal'
            });
            setOpenAdd(false);
            setFileName('');
            setFileUrl('');
            loadFiles();
        } catch (err) {
            console.error(err);
            alert('Failed to add file link');
        }
        setUploading(false);
    };

    const handleDelete = async (fileId) => {
        if (!window.confirm('Delete this file attachment?')) return;
        try {
            await api.delete(`/admin/jobs/${jobId}/files/${fileId}`);
            loadFiles();
        } catch (err) {
            console.error(err);
            alert('Failed to delete file');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={() => setOpenAdd(true)}
                >
                    Attach File / Link
                </Button>
            </Box>

            {files.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, border: '1px dashed #E2E8F0', borderRadius: 3, textAlign: 'center' }}>
                    <InsertDriveFile sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                    <Typography color="text.secondary">No files attached yet</Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {files.map((file) => (
                        <Grid item xs={12} sm={6} md={4} key={file.id}>
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 2, 
                                    border: '1px solid #E2E8F0', 
                                    borderRadius: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                    <Box sx={{ mr: 2, display: 'flex' }}>
                                        <FileIcon type={file.file_type} />
                                    </Box>
                                    <Box sx={{ overflow: 'hidden' }}>
                                        <Link 
                                            href={file.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="text.primary"
                                            variant="subtitle2"
                                            fontWeight={600}
                                            noWrap
                                            display="block"
                                        >
                                            {file.file_name}
                                        </Link>
                                        <Typography variant="caption" color="text.secondary">
                                            Added {parseUtc(file.uploaded_at).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <IconButton size="small" href={file.url} target="_blank">
                                        <OpenInNew fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(file.id)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Add File Dialog */}
            <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Attach File Link</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Since we don't have cloud storage configured yet, please paste a URL to the file (Google Drive, Dropbox, S3, etc).
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="File Name"
                        fullWidth
                        variant="outlined"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="File URL"
                        fullWidth
                        variant="outlined"
                        value={fileUrl}
                        onChange={(e) => setFileUrl(e.target.value)}
                        placeholder="https://..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
                    <Button onClick={handleAddFile} variant="contained" disabled={uploading}>
                        Add Link
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default JobFiles;
